import { logger } from "../utils/logger";
import React, { useContext, useReducer, useCallback, ReactNode } from "react";
import { useAppStore } from "../store";
import { apiService } from "../services/api/apiService";
import type { GatewayServiceStatus } from "../types/api";
import { createSharedContext } from "../lib/utils";
import { messageFromCaughtApiError } from "@/lib/utils/response";

export interface ServiceStatus {
  id: string;
  name: string;
  status: "healthy" | "unhealthy" | "unknown";
  uptime: number;
  responseTime: number;
  lastCheck: number;
  metrics: {
    cpu: number;
    memory: number;
    connections: number;
    errorRate: number;
  };
}

export interface ApiState {
  // Service Health
  services: Record<string, ServiceStatus>;
  // Cache Management
  cache: {
    hits: number;
    misses: number;
    size: number;
    keys: string[];
  };
  // System Status
  systemStatus: {
    overall: "healthy" | "degraded" | "unhealthy";
    lastCheck: number;
    uptime: number;
    version: string;
  };
  // Loading States
  loading: {
    healthCheck: boolean;
    serviceHealth: boolean;
    cacheClear: boolean;
  };
  // Error States
  errors: {
    healthCheck: string | null;
    serviceHealth: string | null;
    cacheClear: string | null;
  };
}

export type ApiAction =
  | { type: "SET_SERVICES"; payload: Record<string, ServiceStatus> }
  | { type: "UPDATE_SERVICE"; payload: { id: string; status: ServiceStatus } }
  | { type: "SET_SYSTEM_STATUS"; payload: ApiState["systemStatus"] }
  | { type: "SET_CACHE_STATS"; payload: ApiState["cache"] }
  | {
      type: "SET_LOADING";
      payload: { key: keyof ApiState["loading"]; value: boolean };
    }
  | {
      type: "SET_ERROR";
      payload: { key: keyof ApiState["errors"]; value: string | null };
    }
  | { type: "CLEAR_ERRORS" };

const apiReducer = (state: ApiState, action: ApiAction): ApiState => {
  switch (action.type) {
    case "SET_SERVICES":
      return {
        ...state,
        services: action.payload,
      };
    case "UPDATE_SERVICE":
      return {
        ...state,
        services: {
          ...state.services,
          [action.payload.id]: action.payload.status,
        },
      };
    case "SET_SYSTEM_STATUS":
      return {
        ...state,
        systemStatus: action.payload,
      };
    case "SET_CACHE_STATS":
      return {
        ...state,
        cache: action.payload,
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      };
    case "SET_ERROR":
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.key]: action.payload.value,
        },
      };
    case "CLEAR_ERRORS":
      return {
        ...state,
        errors: {
          healthCheck: null,
          serviceHealth: null,
          cacheClear: null,
        },
      };
    default:
      return state;
  }
};

const initialState: ApiState = {
  services: {},
  cache: {
    hits: 0,
    misses: 0,
    size: 0,
    keys: [],
  },
  systemStatus: {
    overall: "healthy" as const,
    lastCheck: 0,
    uptime: 0,
    version: "1.0.0",
  },
  loading: {
    healthCheck: false,
    serviceHealth: false,
    cacheClear: false,
  },
  errors: {
    healthCheck: null,
    serviceHealth: null,
    cacheClear: null,
  },
};

function mapGatewayToServiceStatus(service: GatewayServiceStatus): ServiceStatus {
  const mappedStatus: "healthy" | "unhealthy" | "unknown" = service.status === "operational" ? "healthy" : service.status === "down" || service.status === "offline" ? "unhealthy" : "unknown";
  const lastCheckNum = typeof service.lastCheck === "number" ? service.lastCheck : new Date(service.lastCheck).getTime() || Date.now();
  const uptime = typeof service.uptime === "number" ? service.uptime : Date.now() - 86400000;
  return {
    id: service.name,
    name: service.name,
    status: mappedStatus,
    uptime,
    responseTime: service.responseTime ?? 0,
    lastCheck: lastCheckNum,
    metrics: {
      cpu: service.metrics?.cpu ?? 0,
      memory: service.metrics?.memory ?? 0,
      connections: service.metrics?.connections ?? 0,
      errorRate: service.metrics?.errorRate ?? 0,
    },
  };
}

function servicesToRealServices(list: GatewayServiceStatus[]): Record<string, ServiceStatus> {
  const realServices: Record<string, ServiceStatus> = {};
  list.forEach((service) => {
    realServices[service.name] = mapGatewayToServiceStatus(service);
  });
  return realServices;
}

function computeOverallStatus(realServices: Record<string, ServiceStatus>): "healthy" | "degraded" | "unhealthy" {
  const services = Object.values(realServices);
  const healthyCount = services.filter((s) => s.status === "healthy").length;
  const totalCount = services.length;
  return healthyCount === totalCount ? "healthy" : healthyCount > totalCount / 2 ? "degraded" : "unhealthy";
}

interface ApiContextType {
  state: ApiState;
  dispatch: React.Dispatch<ApiAction>;
  // Actions
  checkAllServices: () => Promise<void>;
  checkServiceHealth: (serviceId: string) => Promise<ServiceStatus>;
  getCacheStats: () => void;
  clearCache: (pattern?: string) => Promise<void>;
  getSystemStatus: () => Promise<void>;
}

const ApiContext = createSharedContext<ApiContextType | undefined>("__9s_api_react_context__", undefined);

export const ApiProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(apiReducer, initialState);
  const addNotification = useAppStore((state) => state.addNotification);

  // Check system health
  const healthCheck = useCallback(async () => {
    try {
      dispatch({
        type: "SET_LOADING",
        payload: { key: "healthCheck", value: true },
      });
      dispatch({
        type: "SET_ERROR",
        payload: { key: "healthCheck", value: null },
      });

      // REAL API call via unified apiService
      const [healthResponse, servicesResponse] = await Promise.all([apiService.getSystemHealth(), apiService.getServiceStatuses()]);

      if (!healthResponse.success) {
        throw new Error("Failed to get system health data");
      }

      const servicesPayload = Array.isArray(servicesResponse) ? servicesResponse : [];
      const realServices = servicesToRealServices(servicesPayload);
      dispatch({ type: "SET_SERVICES", payload: realServices });

      const overall = computeOverallStatus(realServices);
      dispatch({
        type: "SET_SYSTEM_STATUS",
        payload: {
          overall,
          lastCheck: Date.now(),
          uptime: Date.now() - 86400000,
          version: "1.0.0",
        },
      });

      addNotification({
        type: "success",
        title: "Health Check Complete",
        message: `All services checked. Overall status: ${overall}`,
        read: false,
      });
    } catch (error) {
      const errorMessage = messageFromCaughtApiError(error);
      dispatch({
        type: "SET_ERROR",
        payload: { key: "healthCheck", value: errorMessage },
      });
      addNotification({
        type: "error",
        title: "Health Check Failed",
        message: `Failed to check services: ${errorMessage}`,
        read: false,
      });
    } finally {
      dispatch({
        type: "SET_LOADING",
        payload: { key: "healthCheck", value: false },
      });
    }
  }, [addNotification]);

  // Check individual service health
  const checkServiceHealth = useCallback(
    async (serviceId: string): Promise<ServiceStatus> => {
      try {
        dispatch({
          type: "SET_LOADING",
          payload: { key: "serviceHealth", value: true },
        });
        dispatch({
          type: "SET_ERROR",
          payload: { key: "serviceHealth", value: null },
        });

        // ✅ FIXED: Use apiService instead of direct fetch
        const response = await apiService.getServiceHealth(serviceId);
        if (response.success) {
          const status = response.data as ServiceStatus;
          dispatch({
            type: "UPDATE_SERVICE",
            payload: { id: serviceId, status },
          });
          return status;
        } else {
          // Fallback to error status if API fails
          const status: ServiceStatus = {
            id: serviceId,
            name: serviceId,
            status: "unhealthy",
            uptime: 0,
            responseTime: 0,
            lastCheck: Date.now(),
            metrics: { cpu: 0, memory: 0, connections: 0, errorRate: 100 },
          };
          dispatch({
            type: "UPDATE_SERVICE",
            payload: { id: serviceId, status },
          });
          return status;
        }
      } catch (error) {
        const errorMessage = messageFromCaughtApiError(error);
        dispatch({
          type: "SET_ERROR",
          payload: { key: "serviceHealth", value: errorMessage },
        });
        const errorStatus: ServiceStatus = {
          id: serviceId,
          name: serviceId,
          status: "unknown",
          uptime: 0,
          responseTime: 0,
          lastCheck: Date.now(),
          metrics: { cpu: 0, memory: 0, connections: 0, errorRate: 100 },
        };
        addNotification({
          type: "error",
          title: "Service Health Check Failed",
          message: `${serviceId}: ${errorMessage}`,
          read: false,
        });

        return errorStatus;
      } finally {
        dispatch({
          type: "SET_LOADING",
          payload: { key: "serviceHealth", value: false },
        });
      }
    },
    [addNotification, dispatch]
  );

  // Get cache statistics from API
  const getCacheStats = useCallback(async () => {
    try {
      // ✅ FIXED: Use apiService instead of direct fetch
      const response = await apiService.getCacheStats();
      if (response.success) {
        const stats = (response.data as ApiState["cache"]) || {
          hits: 0,
          misses: 0,
          size: 0,
          keys: [],
        };
        dispatch({ type: "SET_CACHE_STATS", payload: stats });
      } else {
        // Fallback to empty stats if API fails
        dispatch({
          type: "SET_CACHE_STATS",
          payload: { hits: 0, misses: 0, size: 0, keys: [] },
        });
      }
    } catch (error) {
      logger.error("Failed to get cache stats", undefined, error instanceof Error ? error : new Error(String(error)));
      // Fallback to empty stats
      dispatch({
        type: "SET_CACHE_STATS",
        payload: { hits: 0, misses: 0, size: 0, keys: [] },
      });
    }
  }, [dispatch]);

  // Clear cache
  const clearCache = useCallback(
    async (pattern?: string) => {
      try {
        dispatch({
          type: "SET_LOADING",
          payload: { key: "cacheClear", value: true },
        });
        dispatch({
          type: "SET_ERROR",
          payload: { key: "cacheClear", value: null },
        });

        const response = await apiService.clearCache();
        if (!response.success) {
          throw new Error(`Failed to clear cache: ${response.error}`);
        }

        const updatedStatsResponse = await apiService.getCacheStats();
        const stats = updatedStatsResponse.success
          ? (updatedStatsResponse.data as ApiState["cache"]) || {
              hits: 0,
              misses: 0,
              size: 0,
              keys: [],
            }
          : { hits: 0, misses: 0, size: 0, keys: [] };
        dispatch({ type: "SET_CACHE_STATS", payload: stats });

        addNotification({
          type: "success",
          title: "Cache Cleared",
          message: pattern ? `Cache cleared for pattern: ${pattern}` : "All cache cleared",
          read: false,
        });
      } catch (error) {
        const errorMessage = messageFromCaughtApiError(error);
        dispatch({
          type: "SET_ERROR",
          payload: { key: "cacheClear", value: errorMessage },
        });
        addNotification({
          type: "error",
          title: "Cache Clear Failed",
          message: `Failed to clear cache: ${errorMessage}`,
          read: false,
        });
      } finally {
        dispatch({
          type: "SET_LOADING",
          payload: { key: "cacheClear", value: false },
        });
      }
    },
    [addNotification, dispatch]
  );

  // Get system status from API
  const getSystemStatus = useCallback(async () => {
    try {
      // ✅ FIXED: Use apiService instead of direct fetch
      const response = await apiService.getSystemStatus();
      if (response.success) {
        const status = (response.data as ApiState["systemStatus"]) || {
          overall: "healthy" as const,
          lastCheck: Date.now(),
          uptime: 99.9,
          version: "3.0.0",
        };
        dispatch({ type: "SET_SYSTEM_STATUS", payload: status });
      } else {
        // Fallback to basic status if API fails
        const status = {
          overall: "unhealthy" as const,
          lastCheck: Date.now(),
          uptime: 0,
          version: "unknown",
        };
        dispatch({ type: "SET_SYSTEM_STATUS", payload: status });
      }
    } catch (error) {
      const errorMessage = messageFromCaughtApiError(error);
      addNotification({
        type: "error",
        title: "System Status Failed",
        message: `Failed to get system status: ${errorMessage}`,
        read: false,
      });
    }
  }, [addNotification, dispatch]);

  const contextValue: ApiContextType = {
    state,
    dispatch,
    checkAllServices: healthCheck, // Renamed to checkAllServices
    checkServiceHealth,
    getCacheStats,
    clearCache,
    getSystemStatus,
  };

  return <ApiContext.Provider value={contextValue}>{children}</ApiContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useApiContext = (): ApiContextType => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApiContext must be used within an ApiProvider");
  }
  return context;
};
